import { observer } from 'mobx-react-lite'
import { type ReactNode, useEffect } from 'react'
import { Alert, Typography } from 'shared/ui'
import { DropdownCards } from 'entities/Billing/ui/DropdownCards'
import { PriceInfo } from 'entities/Billing'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { type PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import styles from './styles.module.scss'

type IPaymentProps = {
  paymentStore: PaymentCommonStore
  formattedNumber: string
  stepLoading: boolean
  confirmNumber: () => void
  numberDescription?: ReactNode
  licensesRequired?: number
}

export const PaymentCommon = observer(
  ({
    paymentStore,
    formattedNumber,
    stepLoading,
    confirmNumber,
    numberDescription,
    licensesRequired = 1,
  }: IPaymentProps) => {
    const {
      numbersPriceInfoStore,
      dropdownCardsStore,
      paymentError,
      noAvailableError,
      chooseNumberHandler,
      isFirstNumber,
      licensesCount,
    } = paymentStore
    const { priceInfo } = numbersPriceInfoStore

    useEffect(() => {
      paymentStore.fetchPriceData(licensesRequired)
    }, [licensesRequired])

    if (!priceInfo && !isFirstNumber && licensesCount <= 0) return null
    const nextButtonText = isFirstNumber ? 'Confirm number' : 'Buy number'

    const Layout = () => {
      if (isFirstNumber) {
        return (
          <Alert
            item={{
              type: 'success',
              desc: 'Your first number is FREE. Additional number - $5.00 / month.',
            }}
          />
        )
      }
      if (priceInfo && priceInfo.total_excluding_tax <= 0) {
        return (
          <Alert
            item={{
              type: 'success',
              desc: 'Today you will not be charged for a new number.',
            }}
          />
        )
      }

      if (paymentStore.licensesCount > 0) {
        return (
          <Alert
            item={{
              type: 'infoBlue',
              title: 'You’ll not be charged for this number.',
              desc: `Your workspace has ${paymentStore.licensesCount} number license${
                paymentStore.licensesCount > 1 ? 's' : ''
              } available.`,
            }}
          />
        )
      }

      return <PriceInfo priceInfoStore={numbersPriceInfoStore} labelPrefix='Number licenses' />
    }

    return (
      <div className={styles.root}>
        <div className={styles.content}>
          {paymentError && (
            <Alert
              className={styles.paymentErrorAlert}
              item={{
                type: 'error',
                title: 'We were unable to process your payment',
                desc: 'Please select a different card',
              }}
            />
          )}
          {noAvailableError && (
            <Alert
              className={styles.paymentErrorAlert}
              rightActions={[
                {
                  variant: 'button',
                  type: 'primary',
                  text: 'Choose number',
                  onClick: chooseNumberHandler,
                },
              ]}
              item={{
                type: 'error',
                title: 'This number is no longer available.',
                desc: 'Please choose a different number.',
              }}
            />
          )}
          <Typography ariaLabel='numberTollFree' variant='body-xl-medium' marginBottom={'4px'}>
            {formattedNumber}
          </Typography>
          <Typography
            ariaLabel='numberTollFreeTitle'
            variant='body-rg-regular'
            color='var(--content-primary-tertiary)'
            marginBottom={'38px'}
          >
            {numberDescription || 'Toll-free number'}
          </Typography>

          <Layout />

          {paymentError && (
            <div className={styles.dropdownCards}>
              <DropdownCards store={dropdownCardsStore} />
            </div>
          )}

          <NextButton
            text={nextButtonText}
            disabled={stepLoading}
            loading={stepLoading}
            onClick={confirmNumber}
          />
        </div>
      </div>
    )
  }
)
